// `TrialExpiredSplash` — full-page hard-block splash (US-200).
//
// Mounted at `/billing-locked` (Wave B6). When the subscription is in
// `hard_block` access tier, `<HardBlockRedirect />` pushes the user here.
// The page renders a single big "Reactivate" CTA + a quiet link back to the
// inbox (which itself remains read-only behind `<TrialExpiredOverlay />`).
//
// Rationale: the bare `/settings/billing` redirect (pre-B6) dropped users
// into a noisy settings tree without explaining the lock — this splash is
// the explicit "your trial expired" stop-gap before the upgrade flow.

import { useT } from '@barel/i18n';
import { Button } from '@barel/ui';
import { Lock } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';

export function TrialExpiredSplash() {
  const t = useT('billing');
  const navigate = useNavigate();

  return (
    <div
      data-region="trial-expired-splash"
      role="alert"
      className="flex min-h-screen flex-col items-center justify-center gap-4 bg-bg-base px-6 text-center"
    >
      <Lock size={36} className="text-tertiary" aria-hidden />
      <h1 className="text-2xl font-bold text-primary">{t('splash.title')}</h1>
      <p className="max-w-md text-sm text-secondary">{t('splash.body')}</p>
      <div className="mt-2 flex flex-col items-center gap-2">
        <Button
          variant="primary"
          size="lg"
          onClick={() =>
            void navigate({ to: '/settings/$section', params: { section: 'billing' } })
          }
          data-testid="trial-expired-splash-cta"
        >
          {t('splash.cta')}
        </Button>
        <button
          type="button"
          className="text-xs text-tertiary underline hover:text-secondary"
          data-testid="trial-expired-splash-secondary"
          onClick={() =>
            void navigate({
              to: '/inbox',
              search: { accountId: undefined, mode: undefined, selectedId: undefined },
            })
          }
        >
          {t('splash.secondaryCta')}
        </button>
      </div>
    </div>
  );
}
